// StoreIssueNote.jsx — Listing Page (UPDATED FOR LOCALSTORAGE HEADERS)
// Fix: handleEdit correctly sets storeuserid from fld_userid column
// Fix: sessionStorage keys are consistent with what Add page reads
// Fix: Add New clears all 3 session keys correctly
// NEW: finYear / branch / user data now comes from localStorage
// NEW: commonHeaders sent in API calls

import React, { useState, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { FaPlus, FaSearch, FaTrash, FaFilter, FaTimes, FaEdit } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

const API = "http://nissi-india.com/api/storeIssueNote";

const StoreIssueNote = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ── LocalStorage values ──────────────────────────────────────────────────────
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = parseInt(localStorage.getItem("branchId") || "0", 10);
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ── Filter state ─────────────────────────────────────────────────────────────
  const today = new Date().toISOString().split("T")[0];
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split("T")[0];

  const [showFilters, setShowFilters] = useState(true);
  const [fromDate, setFromDate] = useState(sevenDaysAgo);
  const [toDate, setToDate] = useState(today);
  const [scrNo, setScrNo] = useState("");
  const [data, setData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ── Search ───────────────────────────────────────────────────────────────────
  const handleSearch = () => {
    if (!branchId) {
      toast.warning("Branch ID not found in localStorage");
      return;
    }

    if (!fromDate || !toDate) {
      toast.warning("Please select From Date and To Date");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      toast.warning("From Date must be less than or equal to To Date");
      return;
    }

    setLoading(true);
    const issueNo = scrNo.trim() === "" ? "%" : scrNo.trim();

    axios
      .get(`${API}/storeIssue_grid`, {
        params: {
          branchid: branchId,
          dtfrom: fromDate,
          dtto: toDate,
          storeIssueNo: issueNo,
        },
        headers: commonHeaders,
      })
      .then((res) => {
        const rows = res.data.data || [];
        setData(rows);
        setSelectedItems([]);

        if (rows.length === 0) toast.info("No records found");
        else toast.success(`Found ${rows.length} record(s)`);
      })
      .catch((err) => {
        console.error("Search error:", err);
        toast.error("Failed to fetch listing");
      })
      .finally(() => setLoading(false));
  };

  // ── Select All / Single ──────────────────────────────────────────────────────
  const toggleAll = (e) =>
    setSelectedItems(e.target.checked ? data.map((d) => d.fld_storesissueid) : []);

  const toggleOne = (id) =>
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );

  // ── Delete ───────────────────────────────────────────────────────────────────
  const handleBulkDelete = () => {
    if (selectedItems.length === 0) {
      toast.warning("No items selected");
      return;
    }

    if (!window.confirm(`Delete ${selectedItems.length} selected record(s)?`)) return;

    setDeleting(true);

    axios
      .delete(`${API}/storesissue_delete`, {
        data: { ids: selectedItems.map(Number) },
        headers: commonHeaders,
      })
      .then(() => {
        toast.success("Deleted successfully");
        setData((prev) =>
          prev.filter((d) => !selectedItems.includes(d.fld_storesissueid))
        );
        setSelectedItems([]);
      })
      .catch((err) => {
        console.error("Delete error:", err);
        toast.error("Failed to delete records");
      })
      .finally(() => setDeleting(false));
  };

  // ── Navigate to Edit ─────────────────────────────────────────────────────────
  // storeid       → display code string (e.g. "STORE/01/0005")
  // storeuserid   → fld_userid from DB row
  // storeissueid  → fld_storesissueid integer PK
  const handleEdit = (item) => {
    const issueNo = item.fld_storesissueno || "";
    const issueId = item.fld_storesissueid || "";
    const createdUserId = item.fld_userid || "";

    sessionStorage.setItem("storeid", issueNo);
    sessionStorage.setItem("storeuserid", String(createdUserId));
    sessionStorage.setItem("storeissueid", String(issueId));

    navigate("/inventory/StoreIssue");
  };

  // ── Add New ──────────────────────────────────────────────────────────────────
  const handleAddNew = () => {
    sessionStorage.removeItem("storeid");
    sessionStorage.removeItem("storeuserid");
    sessionStorage.removeItem("storeissueid");
    navigate("/inventory/StoreIssue");
  };

  const clearFilters = () => {
    setScrNo("");
    setFromDate(sevenDaysAgo);
    setToDate(today);
    setData([]);
    setSelectedItems([]);
  };

  // ── CSS ──────────────────────────────────────────────────────────────────────
  const inputCls = `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-${baseColor}-400 ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelCls = `block text-sm font-medium mb-1 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  return (
    <LayoutWrapper>
      <HeaderBar title="STORE ISSUE NOTE LISTING" />
      <ToastContainer position="top-right" autoClose={3000} />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}
      >
        {/* ── Title Bar ── */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Store Issue Notes
            </h1>
            {data.length > 0 && (
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {data.length} Records
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters((f) => !f)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm transition-colors ${
                isDark
                  ? `border-gray-600 text-white hover:bg-gray-700 ${showFilters ? "bg-gray-700" : ""}`
                  : `border-gray-300 text-gray-700 hover:bg-gray-100 ${showFilters ? "bg-gray-100" : ""}`
              }`}
            >
              <FaFilter />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={handleAddNew}
              className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg text-sm transition-colors`}
            >
              <FaPlus /> Add New
            </button>
          </div>
        </div>

        {/* ── Filters ── */}
        {showFilters && (
          <div
            className={`rounded-xl p-5 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <h3 className={`text-base font-semibold mb-4 ${isDark ? "text-white" : "text-gray-800"}`}>
              Search Filters
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className={labelCls}>
                  From Date <span className="text-red-400">*</span>
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={inputCls}
                />
              </div>

              <div>
                <label className={labelCls}>
                  To Date <span className="text-red-400">*</span>
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={inputCls}
                />
              </div>

              <div>
                <label className={labelCls}>Store Issue No</label>
                <input
                  type="text"
                  value={scrNo}
                  onChange={(e) => setScrNo(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Leave blank to search all"
                  className={inputCls}
                />
              </div>
            </div>

            <div
              className={`flex justify-between items-center mt-5 pt-4 border-t ${
                isDark ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <button
                onClick={clearFilters}
                className={`flex items-center gap-2 text-sm px-3 py-2 rounded ${
                  isDark ? "text-gray-300 hover:text-white" : "text-gray-500 hover:text-gray-800"
                }`}
              >
                <FaTimes /> Clear
              </button>

              <button
                onClick={handleSearch}
                disabled={loading}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-5 py-2 rounded-lg text-sm transition-colors disabled:opacity-50`}
              >
                {loading ? (
                  <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4" />
                ) : (
                  <FaSearch />
                )}
                {loading ? "Searching..." : "Search Records"}
              </button>
            </div>
          </div>
        )}

        {/* ── Bulk Action Bar ── */}
        {data.length > 0 && (
          <div className="flex justify-between items-center mb-3">
            <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {selectedItems.length} of {data.length} selected
            </span>

            <button
              onClick={handleBulkDelete}
              disabled={selectedItems.length === 0 || deleting}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                selectedItems.length === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-red-600 hover:bg-red-700 text-white"
              }`}
            >
              {deleting ? (
                <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4" />
              ) : (
                <FaTrash />
              )}
              Delete Selected
            </button>
          </div>
        )}

        {/* ── Table ── */}
        {data.length > 0 ? (
          <div
            className={`rounded-xl border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className={`bg-${baseColor}-${baseShade} text-white`}>
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={data.length > 0 && selectedItems.length === data.length}
                        onChange={toggleAll}
                        className="rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-xs">S.No</th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-xs">Store Issue No</th>
                    <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-xs">Date</th>
                    <th className="px-4 py-3 text-right font-semibold uppercase tracking-wide text-xs">Total</th>
                    <th className="px-4 py-3 text-center font-semibold uppercase tracking-wide text-xs">Approve</th>
                    <th className="px-4 py-3 text-center font-semibold uppercase tracking-wide text-xs">Action</th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark ? "divide-gray-700 bg-gray-800" : "divide-gray-100 bg-white"
                  }`}
                >
                  {data.map((item, idx) => (
                    <tr
                      key={item.fld_storesissueid}
                      className={`transition-colors ${
                        isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.fld_storesissueid)}
                          onChange={() => toggleOne(item.fld_storesissueid)}
                          className="rounded"
                        />
                      </td>

                      <td className={`px-4 py-3 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        {idx + 1}
                      </td>

                      <td
                        className={`px-4 py-3 font-medium cursor-pointer underline ${
                          isDark ? "text-blue-300" : "text-blue-600"
                        }`}
                        onClick={() => handleEdit(item)}
                      >
                        {item.fld_storesissueno}
                      </td>

                      <td className={`px-4 py-3 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        {item.fld_date
                          ? new Date(item.fld_date).toLocaleDateString("en-GB")
                          : "-"}
                      </td>

                      <td
                        className={`px-4 py-3 text-right ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        {item.totalNew != null
                          ? Number(item.totalNew).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })
                          : "-"}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            item.approve === "Yes"
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {item.approve || "No"}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-blue-500 hover:text-blue-700 transition-colors"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          !loading && (
            <div className={`text-center py-16 ${isDark ? "text-gray-400" : "text-gray-400"}`}>
              <FaSearch className="mx-auto mb-3 text-3xl opacity-30" />
              <p className="text-sm">Use the filters above to search for store issue records.</p>
            </div>
          )
        )}
      </div>
    </LayoutWrapper>
  );
};

export default StoreIssueNote;